// src/components/SegmentBar.jsx
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck, faTimes, faUserEdit, faPen, faTrash, faToggleOn, faToggleOff, faGear } from "@fortawesome/free-solid-svg-icons";
import PrelegentEditorModal from "../modals/PrelegentEditorModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import {
  SwipeableList,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  Type as ListType,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import "../App.css"

const API = "https://api18081113.queroapp.pl";

export default function SegmentBar({
  groups = [],
  loadingGroups = false,
  segments = [],
  setSegments = () => { },
  showInput = false,
  setShowInput = () => { },
  segmentInput = "",
  setSegmentInput = () => { },
  activeStage = null,
  onToggleStage = () => { },
  onRefresh = () => { },
}) {
  const getLS = (k) => {
    try {
      return localStorage.getItem(k) || "";
    } catch {
      return "";
    }
  };

  const secretId =
    typeof window !== "undefined"
      ? getLS("secretId") || getLS("secredId") || getLS("secret-id") || getLS("secred-id")
      : "";

  const [modalRow, setModalRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [prettyNameMap, setPrettyNameMap] = useState({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 5000);
  };

  /* ---------- Helpers ---------- */
  const formatStageLabel = (fullName) => {
    // 1. Try pretty map (DB value)
    const pretty = prettyNameMap[fullName];
    if (pretty && pretty.trim()) return String(pretty);

    // 2. Fallback: Parse string
    if (typeof fullName === 'string') {
      let candidate = fullName;

      // If we know the secretId and it's in the string
      if (secretId && fullName.includes(secretId)) {
        // usually "Name_SecretId_StageId"
        const parts = fullName.split(`_${secretId}`);
        if (parts[0]) {
          candidate = parts[0];
        }
      } else if (fullName.includes('_')) {
        // Blind guess: first part
        candidate = fullName.split('_')[0];
      }

      // Cleanup trailing underscores and replace internal ones with spaces
      return candidate.replace(/_+$/, '').replace(/_/g, ' ');
    }

    return fullName;
  };

  const isActive = (fullName) => activeStage === fullName;
  const toggleActive = (fullName) => {
    onToggleStage(activeStage === fullName ? null : fullName);
  };

  /* ---------- Fetch pretty names ---------- */
  const refreshPrettyNames = async () => {
    if (!secretId) return;
    try {
      const res = await fetch(`${API}/get-prelegent-info/${secretId}`);
      const data = await res.json().catch(() => ({}));
      const arr = Array.isArray(data?.data) ? data.data : [];
      const map = {};
      for (const row of arr) {
        if (row?.segment_name) {
          // Prefer stage_name from DB, explicitly
          map[row.segment_name] = row.stage_name || "";
        }
      }
      setPrettyNameMap(map);
    } catch (e) {
      console.warn("Nie udało się pobrać prelegent-info:", e);
    }
  };

  useEffect(() => {
    refreshPrettyNames();
  }, [secretId]);

  /* ---------- Generate random names ---------- */
  const generateRandomId = (length = 6) => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  /* ---------- Delete logic ---------- */
  const requestDelete = (fullName) => {
    setDeleteTarget(fullName);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const fullName = deleteTarget;

    try {
      const res = await fetch(`${API}/delete-prelegent-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, segment_name: fullName }),
      });

      const data = await res.json();

      if (data.success) {
        onRefresh();
        setSegments(prev => {
          const current = Array.isArray(prev) ? prev : [];
          return current.filter(s => s !== fullName);
        });
      } else {
        console.error("Delete failed:", data.message);
        showError("Nie udało się usunąć segmentu: " + (data.message || "Błąd serwera"));
      }
    } catch (e) {
      console.error("Delete error:", e);
      showError("Błąd połączenia z serwerem.");
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  /* ---------- Add new segment ---------- */
  const handleAddSegment = async () => {
    // console.log("handleAddSegment triggered. Input:", segmentInput, "SecretId:", secretId);

    if (!segmentInput?.trim() || !secretId) {
      // console.warn("Missing input or secretId");
      return;
    }

    const isTrialMode = getLS(`isTrial_${secretId}`) === "true";
    if (isTrialMode && segments.length >= 2) {
      showError("W trybie testowym liczba segmentów jest ograniczona, skontaktuj się z nami aby wykupić wersję pełną.");
      return;
    }

    const stage_name = segmentInput.trim();
    const segment_name = generateRandomId();
    const stageId = generateRandomId();
    const fullName = `${segment_name}_${secretId}_${stageId}`;

    // console.log("Creating segment:", fullName);

    // 1️⃣ Optimistic Update (Natychmiast na ekranie)
    const newEntry = { segment_name: fullName, stage_name: stage_name };

    // Update pretty map for label formatting
    setPrettyNameMap((prev) => ({ ...prev, [fullName]: stage_name }));

    // Update the list via parent state setter
    // Change: append to end instead of prepend
    setSegments((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const updated = [...current, fullName];
      // console.log("Updating segments state. Prev:", current, "New:", updated);
      return updated;
    });

    setSegmentInput("");
    setShowInput(false);

    try {
      // 2️⃣ API Calls in Background
      const tablePromise = fetch(`${API}/create-stage-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, stageName: fullName, stageId }),
      });

      const infoPromise = fetch(`${API}/add-prelegent-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, segment_name: fullName, stage_name }),
      });

      const [tableRes, infoRes] = await Promise.all([tablePromise, infoPromise]);

      if (!tableRes.ok || !infoRes.ok) {
        console.error("Error saving segment to backend");
        showError("Wystąpił błąd podczas zapisywania segmentu na serwerze. Twoje zmiany mogą nie zostać zapisane.");
        // Optionally revert state here if strict consistency is needed
      } else {
        // console.log("Backend updated successfully. Triggering parent refresh.");
        onRefresh(); // <--- Trigger parent refresh to update LiveChat
      }

    } catch (err) {
      console.error("Błąd dodawania segmentu:", err);
      showError("Błąd połączenia z serwerem podczas dodawania segmentu.");
    }
  };

  /* ---------- Inline Editing Logic ---------- */
  const [editingSegment, setEditingSegment] = useState(null);
  const [tempName, setTempName] = useState("");

  const startEditing = (fullName, currentLabel) => {
    setEditingSegment(fullName);
    setTempName(currentLabel);
  };

  const saveEdit = async (fullName) => {
    if (!tempName.trim()) {
      setEditingSegment(null);
      return;
    }

    // Optimistic update
    const oldName = prettyNameMap[fullName];
    setPrettyNameMap(prev => ({ ...prev, [fullName]: tempName }));
    setEditingSegment(null);

    try {
      const res = await fetch(`${API}/update-prelegent-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          secretId,
          segment_name: fullName,
          stage_name: tempName
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to update name");
      }

      // Update parent data if needed, but prettyName is local enough for display
      // onRefresh(); // Optional: might strictly sync
    } catch (err) {
      console.error(err);
      // Revert on error
      setPrettyNameMap(prev => ({ ...prev, [fullName]: oldName }));
      showError("Nie udało się zmienić nazwy segmentu.");
    }
  };

  /* ---------- Swipeable Actions ---------- */
  const trailingActions = (fullName, label) => (
    <TrailingActions>
      <SwipeAction
        onClick={() => {
          setModalRow({ fullName, label });
          setModalOpen(true);
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center bg-secondary text-white shadow-sm"
          style={{
            width: '70px',
            borderRadius: '12px',
            marginRight: '8px', // Space between buttons
            marginLeft: '10px',
            height: '100%',
          }}
        >
          <FontAwesomeIcon icon={faGear} className="fs-5" />
        </div>
      </SwipeAction>

      <SwipeAction
        onClick={() => requestDelete(fullName)}
        destructive={false} // Disable auto-removal animation
      >
        <div
          className="d-flex align-items-center justify-content-center bg-danger text-white shadow-sm"
          style={{
            width: '70px',
            borderRadius: '12px',
            marginLeft: '0px',
            height: '100%',
          }}
        >
          <FontAwesomeIcon icon={faTrash} className="fs-5" />
        </div>
      </SwipeAction >
    </TrailingActions >
  );

  return (
    <div className="admin-box segments-box bg-material h-100 d-flex flex-column overflow-hidden position-relative">
      <div className="d-flex px-1 justify-content-between align-items-center mb-3 flex-shrink-0" style={{ minHeight: '40px' }}>

        {!showInput ? (
          <>
            <h5 className="fw-bold m-0 text-secondary">Segmenty Wydarzenia</h5>
            <button
              onClick={() => setShowInput(true)}
              className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{ width: 36, height: 36, padding: 0 }}
              title="Dodaj segment"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </>
        ) : (
          <div className="w-100 d-flex align-items-center gap-2 draw-open">
            <input
              type="text"
              value={segmentInput}
              onChange={(e) => setSegmentInput(e.target.value)}
              className="form-control rounded-3 border-primary text-dark shadow-sm bg-light"
              placeholder="Wprowadź nazwę segmentu"
              style={{ fontSize: '0.95rem', borderColor: '#0d6efd', borderWidth: '1px' }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddSegment();
                if (e.key === 'Escape') { setShowInput(false); setSegmentInput(""); }
              }}
            />
            <div className="d-flex gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  setShowInput(false);
                  setSegmentInput("");
                }}
                className="btn btn-danger rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white"
                style={{ width: 36, height: 36, padding: 0 }}
                title="Anuluj"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <button
                onClick={handleAddSegment}
                className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm text-white"
                style={{ width: 36, height: 36, padding: 0 }}
                title="Zatwierdź"
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
            </div>
          </div>
        )}
      </div>

      {errorMsg && (
        <div className="alert alert-danger mx-2 mb-2 py-2 small fw-bold text-center border-0 shadow-sm" style={{ animation: 'popIn 0.3s ease-out' }}>
          {errorMsg}
        </div>
      )}

      <div
        className="d-flex flex-column gap-3 flex-grow-1 overflow-y-auto nice-scroll p-1 pb-2"
        style={{
          maskImage: 'linear-gradient(to bottom, black calc(100% - 30px), transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 30px), transparent 100%)'
        }}
      >
        {loadingGroups ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="q-spinner-box-sm">
              <div className="q-spinner">
                <div className="q-mark" style={{ '--i': 0 }}>?</div>
                <div className="q-mark" style={{ '--i': 1 }}>?</div>
                <div className="q-mark" style={{ '--i': 2 }}>?</div>
              </div>
            </div>
            <span className="text-secondary small mt-3 opacity-75">Wczytywanie...</span>
          </div>
        ) : (
          <SwipeableList fullSwipe={false} type={ListType.IOS}>
            {segments.map((fullName, index) => {
              const label = formatStageLabel(fullName);
              const active = isActive(fullName);
              const isEditing = editingSegment === fullName;
              const isFirstItem = index === 0;

              return (
                <SwipeableListItem
                  key={fullName}
                  trailingActions={trailingActions(fullName, label)}
                  className="mb-2"
                >
                  <div
                    className={`re-swipe-item shadow-md rounded-lg p-3 mb-2 d-flex justify-content-between align-items-center transition-standard bg-white border ${active ? 'border-primary' : 'border-light'}`}
                    style={{
                      minHeight: '70px',
                      width: '100%',
                      borderLeft: active ? '4px solid var(--primary-color)' : '1px solid var(--border-color)',
                    }}
                  >
                    {/* Left: Name (Editable) */}
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden me-3" style={{ minWidth: 0 }}>
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0 shadow-sm ${active ? 'bg-primary text-white' : 'bg-soft text-muted border border-light'}`}
                        style={{ width: 36, height: 36, fontWeight: 700, fontSize: '0.85rem' }}
                      >
                        {index + 1}
                      </div>

                      {isEditing ? (
                        <input
                          type="text"
                          value={tempName}
                          onChange={(e) => setTempName(e.target.value)}
                          onBlur={() => saveEdit(fullName)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(fullName);
                            if (e.key === 'Escape') setEditingSegment(null);
                          }}
                          autoFocus
                          className="form-control form-control-md rounded-3 border-primary text-dark fw-bold w-100"
                        />
                      ) : (
                        <div className="flex-grow-1 overflow-hidden text-start" onClick={() => startEditing(fullName, label)}>
                          <div
                            className={`fw-bold text-truncate cursor-pointer text-start ${active ? 'text-primary' : 'text-dark'}`}
                            style={{ fontSize: '1rem', letterSpacing: '-0.1px' }}
                            title="Kliknij, aby edytować nazwę"
                          >
                            {label}
                          </div>
                          <div className="text-muted small mt-0 d-flex align-items-center gap-2" style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                            <span>Przesuń ← aby usunąć</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActive(fullName);
                        }}
                        className={`btn btn-sm fw-bold px-3 py-2 rounded-3 shadow-sm border-2 transition-standard ${active ? 'btn-success text-white border-success' : 'btn-outline-primary bg-white hover-bg-primary'}`}
                        style={{ fontSize: '0.8rem', minWidth: '100px', height: '36px' }}
                      >
                        {active ? (
                          <>
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            AKTYWNY
                          </>
                        ) : (
                          'WŁĄCZ'
                        )}
                      </button>
                    </div>
                  </div>
                </SwipeableListItem>
              );
            })}
          </SwipeableList>
        )}

        {!loadingGroups && segments.length === 0 && !showInput && (
          <div className="text-muted text-center mt-4 small opacity-75">
            Brak segmentów. Kliknij + aby dodać.
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />

      {
        modalRow && (
          <PrelegentEditorModal
            key={modalRow.fullName}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            secretId={secretId}
            segmentRow={modalRow}
            onSaved={() => {
              refreshPrettyNames();
            }}
          />
        )
      }

      {/* Style for iOS Toggle and transitions */}
      <style>{`
        .iOS-toggle { position: relative; display: inline-block; width: 44px; height: 24px; }
        .iOS-toggle input { opacity: 0; width: 0; height: 0; }

        .iOS-toggle .slider {
            position: absolute; cursor: pointer;
            top: 0; left: 0; right: 0; bottom: 0;
            background-color: #ccc; transition: .4s;
            border-radius: 34px;
        }
        .iOS-toggle .slider:before {
            position: absolute; content: "";
            height: 18px; width: 18px;
            left: 3px; bottom: 3px;
            background-color: white; transition: .4s;
            border-radius: 50%;
        }
        .iOS-toggle input:checked + .slider { background-color: #0d6efd; }
        .iOS-toggle input:checked + .slider:before { transform: translateX(20px); }

        .hover-text-primary:hover { color: var(--bs-primary)!important; }

        .draw-open {
            animation: drawOpen 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes drawOpen {
            0% { clip-path: inset(0 0 0 100%); opacity: 0; }
            100% { clip-path: inset(0 0 0 0); opacity: 1; }
        }
        /* Custom Swipeable List Styles Correction */
        .swipeable-list-item {
            margin-bottom: 8px !important; /* Spacing between items handled by wrapper margin */
        }
        /* Ensure trailing action has same rounded corners as card if fully swiped?
           Actually list item is the wrapper. Card is inside.
           We want the red background to appear BEHIND the card.
           Swipeable list handles this. We usually match height.
        */
        /* Question Loader */
        .q-spinner-box-sm { position: relative; width: 60px; height: 60px; }
        .q-spinner {
            position: absolute; inset: 0;
            animation: q-spin 3s linear infinite;
        }
        .q-mark {
            position: absolute;
            top: 0; left: 50%;
            margin-left: -10px; width: 20px; height: 20px;
            transform-origin: 50% 30px; /* Radius 30px */
            
            color: #0d6efd;
            font-family: sans-serif;
            font-size: 24px;
            font-weight: 900;
            text-align: center;
            line-height: 1;
            
            transform: rotate(calc(var(--i) * 120deg));
            animation: q-pulse 1s ease-in-out infinite alternate;
            animation-delay: calc(var(--i) * -0.66s);
        }
        @keyframes q-spin { 
            100% { transform: rotate(360deg); } 
        }
        @keyframes q-pulse {
             0% { transform: rotate(calc(var(--i) * 120deg)) scale(0.8); opacity: 0.6; }
             100% { transform: rotate(calc(var(--i) * 120deg)) scale(1.3); opacity: 1; }
        }

        .re-swipe-hint { opacity: 0; transition: opacity 0.3s; }
        .re-swipe-item:hover .re-swipe-hint { opacity: 0.5; }
      `}</style>
    </div >
  );
}
